import { Bot, Check, User, Clock, AlertTriangle, RotateCw, FileText } from "lucide-react";
import dayjs from "dayjs";
import { message } from "../../../types";
import AudioMessage from "./AudioMessage";
import { cn } from "@/lib/utils";

type MessageChatInterfaceProps = {
  message: Omit<message, "id"> & {
    id?: string;
    isSystem?: boolean;
    toPhoneNumber?: string;
    isPrimaryConnection?: boolean;
    isOptimistic?: boolean;
    sourceId?: string;
  };
  onRetry?: () => void;
};

const MessageChatInterface = ({ message, onRetry }: MessageChatInterfaceProps) => {
  const isAI = message.source === "ai_agent";
  const isHumanAgent = message.source === "human_agent";
  // API-originated sends (e.g. an OTP via POST /v2/template/auth/send) are outbound:
  // neither the AI nor a human advisor, but still our side of the conversation.
  const isMerchantApi = message.source === "merchant_api";
  const isOutbound = isAI || isHumanAgent || isMerchantApi;
  const isPending = message.lastStatus === "pending";
  const isFailed = message.lastStatus === "failed";

  const formatTime = (dateTime: string) => {
    return dayjs(dateTime).format("DD/MM/YYYY HH:mm");
  };

  const renderStatusChecks = () => {
    if (!message.lastStatus || !isOutbound) return null;

    const status = message.lastStatus.toLowerCase();

    if (status === "pending") {
      return <Clock className="ml-1 inline size-3 text-gray-400" />;
    }

    if (status === "failed") {
      return (
        <span className="ml-1 inline-flex items-center gap-1">
          <AlertTriangle className="size-3 text-red-500" />
          {onRetry && (
            <button
              aria-label="Reintentar envío"
              onClick={onRetry}
              className="inline-flex min-w-0 items-center justify-center p-1 text-red-500 hover:text-red-700"
            >
              <RotateCw className="size-3" />
            </button>
          )}
        </span>
      );
    }

    if (status === "sent") {
      return <Check className="ml-1 inline size-3 text-gray-400" />;
    }

    if (status === "delivered") {
      return (
        <span className="ml-1 inline-flex -space-x-1">
          <Check className="size-3 text-gray-400" />
          <Check className="size-3 text-gray-400" />
        </span>
      );
    }

    if (status === "read") {
      return (
        <span className="ml-1 inline-flex -space-x-1">
          <Check className="size-3 text-blue-500" />
          <Check className="size-3 text-blue-500" />
        </span>
      );
    }

    return null;
  };

  const getBgColor = () => {
    if (isAI) return "bg-[#E8F5E9]";
    if (isHumanAgent || isMerchantApi) return "bg-[#EBECF0]";
    if (message.source === "customer" && message.toPhoneNumber && !message.isPrimaryConnection) {
      return "bg-[#FFF3E0]";
    }
    return "bg-[#E3F2FD]";
  };

  return (
    <div className={cn("mb-4 flex px-4", isOutbound ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "relative max-w-[70%] rounded-lg p-3 text-gray-800",
          isFailed ? "border border-red-200 bg-red-50" : getBgColor(),
          isPending && "opacity-70",
          message.audio?.url && "min-w-[240px]"
        )}
      >
        {message.toPhoneNumber && (
          <div className="mb-0.5 text-[10px] leading-none">
            <span
              className={cn(
                "rounded px-1 py-px",
                isHumanAgent
                  ? "bg-[#DDDFE8] text-gray-700"
                  : message.isPrimaryConnection
                  ? "bg-blue-100 text-blue-800"
                  : "bg-orange-100 text-orange-800"
              )}
            >
              <span>Para:</span> <span>{message.toPhoneNumber}</span>
            </span>
          </div>
        )}
        {isAI && (
          <div className="mb-2 flex items-center gap-2">
            <Bot className="size-4 text-gray-500" />
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
              Agente IA
            </span>
          </div>
        )}
        {isHumanAgent && (
          <div className="mb-2 flex items-center gap-2">
            <User className="size-4 text-gray-500" />
            <span className="rounded bg-[#DDDFE8] px-1.5 py-0.5 text-xs text-gray-700">
              {message.sourceId && message.sourceId !== "unknown" ? message.sourceId : "Agente humano"}
            </span>
          </div>
        )}
        {isMerchantApi && (
          <div className="mb-2 flex items-center gap-2">
            <User className="size-4 text-gray-500" />
            <span className="rounded bg-[#DDDFE8] px-1.5 py-0.5 text-xs text-gray-700">API</span>
          </div>
        )}
        {message.audio?.url ? (
          <AudioMessage audio={message.audio} isCustomer={!isOutbound} />
        ) : message.image?.url ? (
          <div>
            <img
              src={message.image.url}
              alt="Imagen compartida"
              style={{
                maxWidth: "100%",
                maxHeight: "400px",
                borderRadius: "8px",
                objectFit: "contain",
              }}
            />
          </div>
        ) : message.video?.url ? (
          <div>
            <video
              src={message.video.url}
              controls
              style={{
                maxWidth: "100%",
                maxHeight: "400px",
                borderRadius: "8px",
              }}
            >
              Tu navegador no soporta el elemento de video.
            </video>
          </div>
        ) : message.document?.url ? (
          <a
            href={message.document.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2"
          >
            <FileText className="size-5 text-gray-600" />
            <span className="truncate text-sm">{message.content?.trim() || "Documento"}</span>
          </a>
        ) : (
          <p className="text-sm leading-relaxed">{message.content}</p>
        )}
        <div
          className={cn(
            "mt-2 flex items-center",
            isOutbound ? "justify-end" : "justify-start"
          )}
        >
          <span className="text-xs text-gray-500">{formatTime(message.createdAt)}</span>
          {renderStatusChecks()}
        </div>
      </div>
    </div>
  );
};

export default MessageChatInterface;
